import { useState, useCallback, useRef, useEffect } from 'preact/hooks'
import { Sparkles, Loader2, AlertCircle, CheckCircle, FileText, Zap, ShieldAlert } from 'lucide-preact'
import { useKnowledgeBase } from '@/hooks/useKnowledgeBase'
import { useKnowledgeBaseSignalR } from '@/hooks/useKnowledgeBaseSignalR'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { knowledgeBaseApi } from '@/api/knowledgeBase'
import { KnowledgeBaseCategoryRow } from './KnowledgeBaseCategoryRow'
import type { KnowledgeBaseLevel } from '@/types/knowledgeBase'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface Props {
  clientCompanyId: string
  workspaceId?: string
  campaignId?: string
  level: KnowledgeBaseLevel
  /** Whether the current user is a company admin (controls AI Populate visibility) */
  isAdmin?: boolean
}

export function KnowledgeBasePanel({ clientCompanyId, workspaceId, campaignId, level, isAdmin }: Props) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [populating, setPopulating] = useState(false)
  const [showPopulateMenu, setShowPopulateMenu] = useState(false)
  const [populateOverwrite, setPopulateOverwrite] = useState(false)
  const populateMenuRef = useRef<HTMLDivElement>(null)
  const { showSuccess, showError, showInfo } = useToast()
  const { userProfile } = useAuth()
  const { canEdit: workspaceCanEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
  // Workspace/campaign KB editing requires workspace edit capability; company-level KB
  // uses the same admin flag the panel already uses for AI Populate (the server enforces
  // RequireCustomerAccess on company KB regardless).
  const userCanEdit = level === 'Company' ? !!isAdmin : workspaceCanEdit

  const {
    categories,
    loading,
    error,
    activeEntry,
    entryLoading,
    fetchEntry,
    upsertEntry,
    deleteOverride,
    uploadFile,
    deleteFile,
    saving,
    refetchSummary,
  } = useKnowledgeBase({ clientCompanyId, workspaceId, campaignId, level })

  // Listen for async AI populate completion via SignalR
  const handlePopulateCompleted = useCallback((notification: { populatedCount: number; skippedCount: number }) => {
    showSuccess(
      'AI Population Complete',
      `Populated ${notification.populatedCount} categories, skipped ${notification.skippedCount}`
    )
    setPopulating(false)
    refetchSummary()
    // Refresh the expanded entry if one is open
    if (expandedCategory) {
      fetchEntry(expandedCategory)
    }
  }, [showSuccess, refetchSummary, expandedCategory, fetchEntry])

  const handlePopulateFailed = useCallback((notification: { errorMessage?: string | null }) => {
    showError('AI Population Failed', notification.errorMessage || 'An error occurred during AI population')
    setPopulating(false)
  }, [showError])

  useKnowledgeBaseSignalR({
    userId: userProfile?.Id,
    onPopulateCompleted: handlePopulateCompleted,
    onPopulateFailed: handlePopulateFailed,
  })

  // Close popover on outside click
  useEffect(() => {
    if (!showPopulateMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (populateMenuRef.current && !populateMenuRef.current.contains(e.target as Node)) {
        setShowPopulateMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPopulateMenu])

  const handleToggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category)
  }

  // Fire-and-forget AI populate — sends request, shows toast, waits for SignalR
  const handleAiPopulateAll = async (overwrite: boolean) => {
    setShowPopulateMenu(false)
    setPopulating(true)
    const res = await knowledgeBaseApi.aiPopulate(clientCompanyId, {
      OverwriteExisting: overwrite,
    })
    if (res.error) {
      showError('Error', res.error)
      setPopulating(false)
    } else {
      showInfo('AI Population Started', 'Scraping website and populating categories. You will be notified when complete.')
    }
  }

  const handleAiPopulateCategory = async (category: string) => {
    setPopulating(true)
    const res = await knowledgeBaseApi.aiPopulateCategory(clientCompanyId, category, {
      OverwriteExisting: true,
    })
    if (res.error) {
      showError('Error', res.error)
      setPopulating(false)
    } else {
      showInfo('AI Population Started', `Repopulating "${category}". You will be notified when complete.`)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16 text-red-500">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">{error}</span>
      </div>
    )
  }

  const populatedCount = categories.filter(c => c.IsPopulated).length
  const totalCount = categories.length
  const completionPct = totalCount > 0 ? Math.round((populatedCount / totalCount) * 100) : 0
  const totalFiles = categories.reduce((sum, c) => sum + c.FileCount, 0)

  // Most recent AI generation timestamp across all categories
  const lastAiSync = categories.reduce<string | null>((latest, c) => {
    if (!c.AiGeneratedAt) return latest
    if (!latest) return c.AiGeneratedAt
    return c.AiGeneratedAt > latest ? c.AiGeneratedAt : latest
  }, null)

  const formatLastSync = (dateStr: string | null): string => {
    if (!dateStr) return '--'
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  // At workspace/campaign level, check if company-level KB has any content
  const isNothingPopulated = populatedCount === 0
  const showSetupBanner = level !== 'Company' && isNothingPopulated

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Knowledge Base</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Manage key brand and marketing knowledge
            {level !== 'Company' && ' — inherits from parent levels'}
          </p>
        </div>

        {/* AI Populate All button — company level, admin only */}
        {level === 'Company' && isAdmin && (
          <div className="relative self-start" ref={populateMenuRef}>
            <button
              type="button"
              onClick={() => setShowPopulateMenu(!showPopulateMenu)}
              disabled={populating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {populating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {populating ? 'Populating...' : 'AI Populate All'}
            </button>

            {showPopulateMenu && (
              <div className="absolute right-0 mt-2 w-72 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-10 p-4 space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Scrape company website and populate all AI-eligible categories.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={populateOverwrite}
                    onChange={(e) => setPopulateOverwrite((e.target as HTMLInputElement).checked)}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Overwrite existing content</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleAiPopulateAll(populateOverwrite)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Start Population
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Setup banner for workspace/campaign when nothing is populated */}
      {showSetupBanner && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Knowledge Base not configured
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
              The Knowledge Base should be populated at the company level first. Ask an administrator to go to Admin Settings and use AI Populate or manually add content.
            </p>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Completion */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{completionPct}%</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Completion</div>
            </div>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
            {populatedCount} of {totalCount} sections completed
          </div>
        </div>

        {/* Total files */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{totalFiles}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Documents Uploaded</div>
            </div>
          </div>
        </div>

        {/* Last Sync (stubbed) */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{formatLastSync(lastAiSync)}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Last AI Sync</div>
            </div>
          </div>
        </div>
      </div>

      {/* Category accordion list */}
      <div className="space-y-2">
        {categories.map((cat) => (
          <KnowledgeBaseCategoryRow
            key={cat.Category}
            categoryStatus={cat}
            level={level}
            isExpanded={expandedCategory === cat.Category}
            onToggle={() => handleToggleCategory(cat.Category)}
            entry={expandedCategory === cat.Category ? activeEntry : null}
            entryLoading={expandedCategory === cat.Category && entryLoading}
            onFetchEntry={fetchEntry}
            onSave={upsertEntry}
            onDeleteOverride={deleteOverride}
            onUploadFile={uploadFile}
            onDeleteFile={deleteFile}
            saving={saving}
            clientCompanyId={clientCompanyId}
            userCanEdit={userCanEdit}
            onAiPopulate={level === 'Company' && isAdmin && cat.Config.AiPopulatable && !populating ? handleAiPopulateCategory : undefined}
          />
        ))}
      </div>
    </div>
  )
}
